// SearchHealthCard.tsx — CTSK-504. Read-only "Search health" card under
// Settings → Documents for admins (settings:manage). Warnings first, then
// config / coverage / indexes / recent latency from
// GET /admin/search/diagnostics, plus an "Explain a query" box
// (POST /admin/search/explain). No charts, no polling — Refresh button.
// Hidden when the API's document search flag is off.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiSingle } from '../lib/api.js';
import { useFeatures } from '../lib/features.js';

interface Warning { code: string; message: string }
interface Diagnostics {
  config: {
    envFlag: boolean;
    instanceId: string;
    uptimeSeconds: number;
    apiVersion: string;
    latestMigration: string | null;
    tuning: { overridden: string[] };
  };
  coverage: {
    liveDocuments: number;
    nullVector: number;
    emptyVector: number;
    /** CTSK-506 — documents with a group, collection or folder, and how many of them have a labels vector. */
    labelledDocuments?: number;
    labelsVectorPresent?: number;
    labelsVectorMissing?: number;
  };
  indexes: Array<{ name: string; exists: boolean; sizeBytes: number; indisvalid: boolean | null; idxScan: number | null }>;
  recent: {
    scope: string;
    count: number;
    p50TotalMs: number | null;
    p95TotalMs: number | null;
    p99TotalMs: number | null;
    zeroResultRate: number | null;
    trgmShare: number | null;
  };
  warnings: Warning[];
}
interface ExplainResult {
  planningMs: number;
  executionMs: number;
  indexesUsed: string[];
  seqScanOnDocuments: boolean;
  sharedHitBlocks: number;
  sharedReadBlocks: number;
  warnings: Warning[];
}

const fmt = (n: number | null | undefined, suffix = '') => (n === null || n === undefined ? '—' : `${n}${suffix}`);
const pct = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`);
const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1)} MB`;

export function SearchHealthCard({ canView }: { canView: boolean }) {
  const features = useFeatures();
  // Wait for /meta/features: until it answers, `documentSearch` is the build-time
  // Vite fallback, which can be true on a flag-off API (a wasted admin 404 on mount).
  const enabled = canView && features.resolved && features.documentSearch;
  const q = useQuery({
    queryKey: ['admin', 'search', 'diagnostics'],
    queryFn: () => apiSingle<Diagnostics>('/admin/search/diagnostics'),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const [text, setText] = useState('');
  const explain = useMutation({
    mutationFn: (query: string) => apiSingle<ExplainResult>('/admin/search/explain', { method: 'POST', body: { q: query } }),
  });

  if (!enabled) return null;
  const d = q.data;

  return (
    <section data-testid="search-health" aria-label="Search health" style={{ margin: '16px 0', padding: 12, border: '1px solid var(--border-strong)', borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Search health</h3>
        <button type="button" className="btn" onClick={() => q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {q.isError && <p role="alert">Could not load search diagnostics.</p>}
      {d && (
        <>
          <div data-testid="search-health-warnings" style={{ margin: '10px 0' }}>
            {d.warnings.length === 0 ? (
              <span>No warnings.</span>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {d.warnings.map((w) => (
                  <li key={w.code}><strong>{w.code}</strong> — {w.message}</li>
                ))}
              </ul>
            )}
          </div>

          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13, margin: 0 }}>
            <dt>Version</dt><dd>{d.config.apiVersion} · migration {d.config.latestMigration ?? '—'}</dd>
            <dt>Process</dt><dd>{d.config.instanceId.slice(0, 8)} · up {Math.round(d.config.uptimeSeconds / 60)} min</dd>
            <dt>Tuning</dt><dd>{d.config.tuning.overridden.length === 0 ? 'all defaults' : `overridden: ${d.config.tuning.overridden.join(', ')}`}</dd>
            <dt>Coverage</dt><dd>{d.coverage.liveDocuments} documents · {d.coverage.nullVector} without a search vector · {d.coverage.emptyVector} empty
              {d.coverage.labelledDocuments !== undefined && (
                <> · {d.coverage.labelledDocuments} labelled, {d.coverage.labelsVectorMissing ?? 0} without a label vector</>
              )}
            </dd>
            {d.indexes.map((i) => (
              <div key={i.name} style={{ display: 'contents' }}>
                <dt>{i.name}</dt>
                <dd>{i.exists ? `${mb(i.sizeBytes)} · ${i.indisvalid ? 'valid' : 'INVALID'} · ${fmt(i.idxScan)} scans` : 'missing'}</dd>
              </div>
            ))}
            <dt>Recent searches</dt>
            <dd>
              {d.recent.count} · p50 {fmt(d.recent.p50TotalMs, ' ms')} · p95 {fmt(d.recent.p95TotalMs, ' ms')} · p99 {fmt(d.recent.p99TotalMs, ' ms')} · no results {pct(d.recent.zeroResultRate)} · substring share {pct(d.recent.trgmShare)}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.recent.scope}</div>
            </dd>
          </dl>
        </>
      )}

      <form
        style={{ marginTop: 12, display: 'flex', gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim().length >= 2) explain.mutate(text.trim());
        }}
      >
        <input
          aria-label="Query to explain"
          value={text}
          maxLength={200}
          onChange={(e) => setText(e.target.value)}
          placeholder="Explain a query…"
          style={{ flex: 1, padding: 6, border: '1px solid var(--border-strong)', borderRadius: 4 }}
        />
        <button type="submit" className="btn" disabled={explain.isPending || text.trim().length < 2}>
          {explain.isPending ? 'Running…' : 'Explain'}
        </button>
      </form>
      {explain.isError && <p role="alert">{(explain.error as Error).message}</p>}
      {explain.data && (
        <dl data-testid="search-explain-result" style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13, marginTop: 8 }}>
          <dt>Indexes used</dt><dd>{explain.data.indexesUsed.length ? explain.data.indexesUsed.join(', ') : 'none'}</dd>
          <dt>Seq scan on documents</dt><dd>{explain.data.seqScanOnDocuments ? 'YES' : 'no'}</dd>
          <dt>Planning / execution</dt><dd>{explain.data.planningMs} ms / {explain.data.executionMs} ms</dd>
          <dt>Buffers</dt><dd>{explain.data.sharedHitBlocks} hit · {explain.data.sharedReadBlocks} read</dd>
        </dl>
      )}
    </section>
  );
}
